/* @jsx h */
import { computed, defineComponent, h, ref } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import type { StockItem } from "../Stock/types";
import { unitOptions } from "../Stock/units";
import { WASTE_REASONS, type Dish, type WasteReason } from "./types";

export type WasteInput =
  | { stock_item_id: number; quantity: number; unit: string; reason: WasteReason; note: string | null }
  | { product_id: number; quantity: number; reason: WasteReason; note: string | null };

type Props = {
  stockItems: StockItem[];
  dishes: Dish[];
  saving: boolean;
  onSubmit: (entry: WasteInput) => void;
};

// Log a stock item ("1 bottle, spoiled") or a whole dish ("2 burgers,
// returned") — a dish uses up its recipe.
export const LogWasteForm = defineComponent<Props>({
  name: "LogWasteForm",
  props: ["stockItems", "dishes", "saving", "onSubmit"],
  setup(props) {
    const { t } = useLocalization();
    const kind = ref<"item" | "dish">("item");
    const targetId = ref<number | null>(null);
    const quantity = ref(1);
    const unit = ref("");
    const reason = ref<WasteReason>("spoiled");
    const note = ref("");

    const item = computed(() => props.stockItems.find((s) => s.id === targetId.value));
    const valid = computed(
      () => targetId.value != null && quantity.value > 0 && (kind.value === "dish" || !!unit.value),
    );

    const submit = () => {
      if (!valid.value || targetId.value == null) return;
      const common = { quantity: quantity.value, reason: reason.value, note: note.value.trim() || null };
      props.onSubmit(
        kind.value === "item"
          ? { stock_item_id: targetId.value, unit: unit.value, ...common }
          : { product_id: targetId.value, ...common },
      );
      note.value = "";
    };

    return () => (
      <div class="rounded-lg border border-base-300 bg-base-100 p-4">
        <div class="mb-3 flex items-center gap-2">
          <div class="join">
            {(["item", "dish"] as const).map((k) => (
              <button
                key={k}
                class={["btn btn-sm join-item", kind.value === k ? "btn-primary" : "btn-ghost"].join(" ")}
                onClick={() => {
                  kind.value = k;
                  targetId.value = null;
                }}
              >
                {t(`inventory.waste.kind_${k}`)}
              </button>
            ))}
          </div>
        </div>
        <div class="flex flex-wrap items-end gap-2">
          <label class="block min-w-48 flex-1">
            <span class="mb-1 block text-xs font-medium">
              {kind.value === "item" ? t("inventory.recipe.col_item") : t("inventory.waste.dish")}
            </span>
            <select
              class="select select-bordered select-sm w-full"
              value={targetId.value ?? ""}
              onChange={(e: Event) => {
                const id = Number((e.target as HTMLSelectElement).value);
                targetId.value = id;
                const next = props.stockItems.find((s) => s.id === id);
                if (next) unit.value = next.base_unit;
              }}
            >
              <option value="" disabled>
                {t("inventory.recipe.pick_item")}
              </option>
              {(kind.value === "item" ? props.stockItems : props.dishes).map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              ))}
            </select>
          </label>
          <label class="block">
            <span class="mb-1 block text-xs font-medium">
              {kind.value === "item" ? t("inventory.recipe.col_qty") : t("inventory.waste.portions")}
            </span>
            <input
              type="number"
              min="0"
              step="any"
              class="input input-bordered input-sm w-24"
              value={quantity.value || ""}
              onInput={(e: Event) => (quantity.value = Number((e.target as HTMLInputElement).value))}
            />
          </label>
          {kind.value === "item" && (
            <label class="block">
              <span class="mb-1 block text-xs font-medium">{t("inventory.recipe.col_unit")}</span>
              <select
                class="select select-bordered select-sm w-24"
                disabled={!item.value}
                value={unit.value}
                onChange={(e: Event) => (unit.value = (e.target as HTMLSelectElement).value)}
              >
                {item.value && unitOptions(item.value).map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </label>
          )}
          <label class="block">
            <span class="mb-1 block text-xs font-medium">{t("inventory.waste.reason")}</span>
            <select
              class="select select-bordered select-sm w-36"
              value={reason.value}
              onChange={(e: Event) => (reason.value = (e.target as HTMLSelectElement).value as WasteReason)}
            >
              {WASTE_REASONS.map((r) => (
                <option key={r} value={r}>
                  {t(`inventory.waste.reason_${r}`)}
                </option>
              ))}
            </select>
          </label>
          <label class="block min-w-40 flex-1">
            <span class="mb-1 block text-xs font-medium">{t("inventory.purchases.notes")}</span>
            <input class="input input-bordered input-sm w-full" v-model={note.value} />
          </label>
          <button class="btn btn-primary btn-sm" disabled={!valid.value || props.saving} onClick={submit}>
            {t("inventory.waste.log")}
          </button>
        </div>
      </div>
    );
  },
});
